import { ApiService } from './api';

// Create a separate API service instance for auth/permissions endpoints
const authApiService = new ApiService();
// Override the base URL to use the auth API URL
authApiService['baseURL'] = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1';

export interface FeaturePermissions {
  ai_insights: boolean;
  ai_analysis: boolean;
  video_analysis: boolean;
  newsletter_analytics: boolean;
  competitor_benchmarking: boolean;
  brand_scout: boolean;
  instagram_graph_api: boolean;
  broadcast_analysis: boolean;
}

export interface SubscriptionTier {
  id: number;
  name: string;
  displayName: string;
  version: number;
}

export interface SubscriptionLimits {
  max_posts: number;
  max_users: number;
  max_newletters: number;
  max_competitors: number;
  max_monthly_scrapes: number;
  max_social_accounts: number;
  post_retention_days: number;
  initial_scrape_limit: number;
  max_monthly_image_analyses: number;
  max_monthly_video_analyses: number;
  post_scrape_frequency_per_day: number;
  competitor_scrape_frequency_per_week: number;
}

export interface PermissionsData {
  organizationId: string;
  tier: SubscriptionTier;
  subscriptionStatus: string;
  features: FeaturePermissions;
  limits: SubscriptionLimits;
  effectiveLimits: SubscriptionLimits;
  hasCustomLimits: boolean;
  customLimits: SubscriptionLimits | null;
}

export interface PermissionsResponse {
  success: boolean;
  data: PermissionsData;
  error?: {
    message: string;
  };
}

class PermissionsService {
  /**
   * Get feature permissions for an organization
   */
  async getFeaturePermissions(organizationId: string): Promise<PermissionsResponse> {
    try {
      const response = await authApiService.get<PermissionsResponse>(`/features/permissions`, {
        organizationId
      });
      return response;
    } catch (error) {
      console.error('Failed to fetch feature permissions:', error);
      return {
        success: false,
        data: {
          organizationId: '',
          tier: { id: 0, name: '', displayName: '', version: 0 },
          subscriptionStatus: 'inactive',
          features: {
            ai_insights: false,
            ai_analysis: false,
            video_analysis: false,
            newsletter_analytics: false,
            competitor_benchmarking: false,
            brand_scout: false,
            instagram_graph_api: false,
            broadcast_analysis: false
          },
          limits: {
            max_posts: 0,
            max_users: 0,
            max_newletters: 0,
            max_competitors: 0,
            max_monthly_scrapes: 0,
            max_social_accounts: 0,
            post_retention_days: 0,
            initial_scrape_limit: 0,
            max_monthly_image_analyses: 0,
            max_monthly_video_analyses: 0,
            post_scrape_frequency_per_day: 0,
            competitor_scrape_frequency_per_week: 0
          },
          effectiveLimits: {
            max_posts: 0,
            max_users: 0,
            max_newletters: 0,
            max_competitors: 0,
            max_monthly_scrapes: 0,
            max_social_accounts: 0,
            post_retention_days: 0,
            initial_scrape_limit: 0,
            max_monthly_image_analyses: 0,
            max_monthly_video_analyses: 0,
            post_scrape_frequency_per_day: 0,
            competitor_scrape_frequency_per_week: 0
          },
          hasCustomLimits: false,
          customLimits: null
        },
        error: {
          message: error instanceof Error ? error.message : 'Failed to fetch permissions'
        }
      };
    }
  }

  /**
   * Check if a specific feature is enabled for an organization
   */
  async hasFeature(organizationId: string, featureName: keyof FeaturePermissions): Promise<boolean> {
    try {
      const response = await this.getFeaturePermissions(organizationId);
      if (response.success && response.data.features) {
        return response.data.features[featureName] === true;
      }
      return false;
    } catch (error) {
      console.error(`Failed to check feature ${featureName}:`, error);
      return false;
    }
  }

  /**
   * Check if video analysis feature is enabled
   */
  async hasVideoAnalysis(organizationId: string): Promise<boolean> {
    return this.hasFeature(organizationId, 'video_analysis');
  }
}

export const permissionsService = new PermissionsService();